import { useEffect, useRef, useState } from 'react';

const pickMime = () => {
  const opts = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  return opts.find(t => window.MediaRecorder?.isTypeSupported?.(t)) || '';
};

// Registrazione vocale via MediaRecorder: start → stop(send) / cancel.
export function useVoiceRecorder({ maxSeconds = 180, onDone }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const t0Ref = useRef(0);
  const cancelRef = useRef(false);
  const timerRef = useRef(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const cleanup = () => {
    clearInterval(timerRef.current);
    recRef.current?.stream?.getTracks().forEach(t => t.stop());
    recRef.current = null;
    setRecording(false);
    setElapsed(0);
  };

  const start = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = pickMime();
    const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
    chunksRef.current = [];
    cancelRef.current = false;
    rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const duration = (Date.now() - t0Ref.current) / 1000;
      const type = rec.mimeType || mime || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      cleanup();
      if (!cancelRef.current && duration >= 0.6) onDoneRef.current?.(blob, duration);
    };
    recRef.current = rec;
    t0Ref.current = Date.now();
    rec.start(250);
    setRecording(true);
    timerRef.current = setInterval(() => {
      const s = (Date.now() - t0Ref.current) / 1000;
      setElapsed(s);
      if (s >= maxSeconds) rec.stop();
    }, 200);
  };

  const stop = () => { if (recRef.current?.state === 'recording') recRef.current.stop(); };
  const cancel = () => { cancelRef.current = true; stop(); };

  useEffect(() => () => { cancelRef.current = true; stop(); clearInterval(timerRef.current); }, []);

  return { recording, elapsed, start, stop, cancel };
}

export const fmtDuration = (s) => {
  const n = Math.max(0, Math.round(s || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};
